import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Lock, ArrowLeft } from 'lucide-react';
import { RESET_STORE_PASSWORD } from '../graphql/queries';

export const ResetPassword: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [reset, { loading }] = useMutation(RESET_STORE_PASSWORD);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    try {
      const { data } = await reset({ variables: { token, password } });
      const r = data?.resetStorePassword;
      if (r?.error) {
        setError(r.message);
        return;
      }
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (err: any) {
      setError(err?.message || 'Connection error');
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl text-center">
          <h1 className="text-lg font-black text-text mb-2">Invalid link</h1>
          <p className="text-xs text-text2 mb-5">The reset link is missing a token.</p>
          <Link to="/forgot-password" className="btn-primary">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A] px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-xs font-semibold text-text2 hover:text-primary mb-5"
        >
          <ArrowLeft size={13} /> Back to sign in
        </Link>
        <h1 className="text-xl font-black text-text mb-1">Set a new password</h1>
        <p className="text-xs text-text2 mb-6">At least 8 characters.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="input-label">New password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" />
              <input
                type="password"
                className="input-field pl-9"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>
          <div>
            <label className="input-label">Confirm password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text3" />
              <input
                type="password"
                className="input-field pl-9"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {done && (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              Password updated. Redirecting to sign-in…
            </div>
          )}

          <button type="submit" disabled={loading || done} className="btn-primary w-full disabled:opacity-60">
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
};
